import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/hooks/useApi';
import type { InviteChannel } from '@/api/types';

/**
 * The Messaged stage for any creator: queue lead or found by the outreacher.
 *
 * Messaging is not a log. It creates no invite and nothing becomes owed; the
 * log happens when the creator replies (Log reply). Backed by
 * POST/DELETE /portal/contacts. Handles are normalised server-side the same
 * way invites are, so `@Name`, `name` and a profile link are one creator.
 */
export interface ContactRecord {
  publicId: string;
  channel: InviteChannel;
  targetIdentifier: string;
  firstContactedAt: string;
  lastContactedAt: string;
  attemptCount: number;
  note: string | null;
}

function invalidateContacts(qc: ReturnType<typeof useQueryClient>): void {
  // "Messaged, no reply" lives under the invites list; lookup history and the
  // queue both show who messaged a creator.
  void qc.invalidateQueries({ queryKey: ['invites'] });
  void qc.invalidateQueries({ queryKey: ['portal', 'outreach-history'] });
  void qc.invalidateQueries({ queryKey: ['portal', 'discovery-prospects'] });
}

export function useRecordContact() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { channel: InviteChannel; targetIdentifier: string; note?: string | null }) =>
      api.post<ContactRecord>('/portal/contacts', input),
    onSuccess: () => invalidateContacts(qc),
  });
}

export function useWithdrawContact() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { channel: InviteChannel; targetIdentifier: string }) =>
      api.del<{ removed: boolean }>('/portal/contacts', input),
    onSuccess: () => invalidateContacts(qc),
  });
}
